import { FilterDataPointMetadataValue, FilterMetadata, MetadataField, MetadataFieldType } from '@/api/generatedApi';
import { EditableMetadataCell } from '@/components/EditableMetadataCell';
import { Ellipsis } from '@/components/Ellipsis';
import { GridColDef, GridRenderCellParams, GridValidRowModel } from '@mui/x-data-grid-pro';
import { getMetadataFilterOperators, MetadataFilterOption } from '@/components/Search/SearchTable/filterOperators';
import { CopiableColumn } from '@/types/copiableColumn';

function toFilterOptions(filterDataPoints?: FilterDataPointMetadataValue[]): MetadataFilterOption[] | undefined {
  if (!filterDataPoints || filterDataPoints.length === 0) {
    return undefined;
  }
  return filterDataPoints
    .filter(point => typeof point.filter === 'string')
    .map(point => ({
      value: point.filter as string,
      count: point.count,
    }));
}

const calculateColumnWidth = (fieldName: string): number => {
  const baseWidth = fieldName.length * 9;
  return Math.min(Math.max(baseWidth, 120), 300);
};

function getMetadataValue<T extends GridValidRowModel, K extends keyof T>(
  row: T,
  fieldName: string,
  accessorKey?: K
): unknown {
  const metadata = accessorKey ? row[accessorKey]?.metadata : row.metadata;
  return metadata && typeof metadata === 'object' ? metadata[fieldName] : undefined;
}

function toDisplayString(value: unknown): string {
  if (value === null || value === undefined) return '';
  switch (typeof value) {
    case 'string': {
      return value;
    }
    case 'number':
    case 'boolean': {
      return String(value);
    }
    default: {
      return JSON.stringify(value);
    }
  }
}

function createValueGetter<T extends GridValidRowModel, K extends keyof T>(
  fieldName: string,
  fieldType: MetadataFieldType | undefined,
  accessorKey?: K
) {
  return (_value: unknown, row: T) => {
    const value = getMetadataValue(row, fieldName, accessorKey);
    if (value === undefined || value === null) {
      return fieldType === MetadataFieldType.Number ? undefined : '';
    }
    return fieldType === MetadataFieldType.Number ? Number(value) : toDisplayString(value);
  };
}

function createRenderCell<T extends GridValidRowModel, K extends keyof T>(
  fieldName: string,
  fieldType: MetadataFieldType | undefined,
  accessorKey?: K,
  editable?: boolean
) {
  const MetadataCell = (parameters: GridRenderCellParams<T>) => {
    if (editable) {
      return <EditableMetadataCell params={parameters} fieldName={fieldName} />;
    }

    const value = getMetadataValue(parameters.row, fieldName, accessorKey);
    if (value === undefined || value === null) {
      return '';
    }

    const displayValue = toDisplayString(value);
    const content = <Ellipsis text={displayValue} />;
    if (fieldType === MetadataFieldType.Url) {
      return (
        <a
          href={displayValue}
          target='_blank'
          rel='noopener noreferrer'
          style={{ color: 'inherit', textDecoration: 'underline' }}
          onClick={event => event.stopPropagation()}
        >
          {content}
        </a>
      );
    }
    return content;
  };
  MetadataCell.displayName = `MetadataCell(${fieldName})`;
  return MetadataCell;
}

function createMetadataColumn<T extends GridValidRowModel, K extends keyof T>(
  fieldName: string,
  fieldType: MetadataFieldType | undefined,
  accessorKey?: K,
  editable?: boolean,
  filterable?: boolean,
  filterOptions?: MetadataFilterOption[],
  onApplyFilter?: () => void
): GridColDef<T> & CopiableColumn<T> {
  return {
    field: `metadata.${fieldName}`,
    headerName: fieldName,
    width: calculateColumnWidth(fieldName),
    sortable: true,
    filterable,
    filterOperators: getMetadataFilterOperators(fieldType, filterOptions, onApplyFilter),
    type: fieldType === MetadataFieldType.Number ? 'number' : 'string',
    valueGetter: createValueGetter(fieldName, fieldType, accessorKey),
    renderCell: createRenderCell(fieldName, fieldType, accessorKey, editable),
    getCopyValue: (row: T) => {
      const value = getMetadataValue(row, fieldName, accessorKey);
      return value ? toDisplayString(value) : undefined;
    },
  };
}

export const getMetadataColumns = <T extends GridValidRowModel, K extends keyof T>({
  metadataFields,
  accessorKey,
  editable = false,
  filterable,
  onApplyFilter,
}: {
  metadataFields: MetadataField[];
  accessorKey?: K;
  editable?: boolean;
  filterable?: boolean;
  onApplyFilter?: () => void;
}): GridColDef[] =>
  metadataFields.map(field =>
    createMetadataColumn<T, K>(field.name, field.type, accessorKey, editable, filterable, undefined, onApplyFilter)
  );

export const getMetadataColumnsFromFilterData = <T extends GridValidRowModel, K extends keyof T>({
  filterDataMetadata,
  metadataFields,
  accessorKey,
  editable = false,
  filterable,
  onApplyFilter,
}: {
  filterDataMetadata?: FilterMetadata;
  metadataFields?: MetadataField[];
  accessorKey?: K;
  editable?: boolean;
  filterable?: boolean;
  onApplyFilter?: () => void;
}): GridColDef[] => {
  if (!filterDataMetadata) {
    return [];
  }
  const fieldTypeMap = new Map(metadataFields?.map(field => [field.name, field.type]));

  return Object.entries(filterDataMetadata).map(([fieldName, filterDataPoints]) => {
    const filterOptions = toFilterOptions(filterDataPoints);
    const fieldType = fieldTypeMap.get(fieldName);
    return createMetadataColumn<T, K>(
      fieldName,
      fieldType,
      accessorKey,
      editable,
      filterable,
      filterOptions,
      onApplyFilter
    );
  });
};

export const insertMetadataColumnsAfterDetails = <T extends GridColDef>(
  baseColumns: T[],
  metadataColumns: GridColDef[]
): T[] => {
  const anchorField = baseColumns.some(col => col.field === 'label') ? 'label' : 'details';
  return insertMetadataColumnsAfterField(baseColumns, metadataColumns, anchorField);
};

export const insertMetadataColumnsAfterField = <T extends GridColDef>(
  baseColumns: T[],
  metadataColumns: GridColDef[],
  field: string
): T[] => {
  const fieldIndex = baseColumns.findIndex(col => col.field === field);
  if (fieldIndex === -1) {
    return [...baseColumns, ...(metadataColumns as T[])];
  }
  return [...baseColumns.slice(0, fieldIndex + 1), ...(metadataColumns as T[]), ...baseColumns.slice(fieldIndex + 1)];
};
